import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Button,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Paper,
  Fade,
} from '@mui/material';
import {
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  Expand as ExpandIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Analytics as AnalysisIcon,
  Home as PropertyIcon,
  TrendingUp as PricingIcon,
  Balance as NegotiationIcon,
  Architecture as FloorPlanIcon,
  LocationCity as NeighborhoodIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch, selectActiveAnalysis, selectAnalysisProgress, cancelAnalysis } from '../../store';
import { AnalysisToolType, AnalysisStatus } from '../../types/property';

interface AnalysisProgressModalProps {
  open: boolean;
  onClose: () => void;
}

const AnalysisProgressModal: React.FC<AnalysisProgressModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  
  const activeAnalysis = useAppSelector(selectActiveAnalysis);
  const progress = useAppSelector(selectAnalysisProgress);
  
  const [isMinimized, setIsMinimized] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Tool display configuration
  const toolConfig: Partial<Record<AnalysisToolType | 'overall', { 
    label: string; 
    icon: React.ReactNode; 
    description: string;
  }>> = {
    overall: {
      label: 'Overall Progress',
      icon: <AnalysisIcon />,
      description: 'Complete property analysis progress',
    },
    propertyParser: {
      label: 'Property Data Parser',
      icon: <PropertyIcon />,
      description: 'Extracting property details and specifications',
    },
    cma: {
      label: 'Comparative Market Analysis',
      icon: <PricingIcon />,
      description: 'Analyzing comparable properties and market trends',
    },
    neighborhood: {
      label: 'Neighborhood Intelligence',
      icon: <NeighborhoodIcon />,
      description: 'Gathering local area insights and demographics',
    },
    pricing: {
      label: 'Pricing Strategy',
      icon: <PricingIcon />,
      description: 'Calculating optimal pricing recommendations',
    },
    negotiation: {
      label: 'Negotiation Support',
      icon: <NegotiationIcon />,
      description: 'Developing negotiation strategies and insights',
    },
    floorPlan: {
      label: 'Floor Plan Analysis',
      icon: <FloorPlanIcon />,
      description: 'Analyzing layout and space utilization',
    },
  };

  // Update elapsed time
  useEffect(() => {
    if (!activeAnalysis?.startTime) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - activeAnalysis.startTime!;
      setElapsedTime(Math.floor(elapsed / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [activeAnalysis?.startTime]);

  // Format time display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status color
  const getStatusColor = (status: AnalysisStatus): string => {
    switch (status) {
      case 'completed': return theme.palette.success.main;
      case 'error': return theme.palette.error.main;
      case 'in_progress': return theme.palette.primary.main;
      default: return theme.palette.grey[500];
    }
  };

  // Get status icon
  const getStatusIcon = (status: AnalysisStatus): React.ReactNode => {
    switch (status) {
      case 'completed': return <CheckCircleIcon color="success" />;
      case 'error': return <ErrorIcon color="error" />;
      case 'in_progress': return <PendingIcon color="primary" />;
      default: return <PendingIcon color="disabled" />;
    }
  };

  // Handle cancel analysis
  const handleCancel = () => {
    if (activeAnalysis) {
      dispatch(cancelAnalysis());
      onClose();
    }
  };

  // Handle minimize/expand
  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Calculate overall progress
  const overallProgress = progress['overall']?.progress || 0;
  const overallStatus = progress['overall']?.status || 'pending';

  // Get estimated completion time
  const getEstimatedCompletion = (): string => {
    if (!activeAnalysis?.estimatedCompletion) return 'Calculating...';
    
    const remaining = Math.max(0, activeAnalysis.estimatedCompletion - Date.now());
    const remainingSeconds = Math.floor(remaining / 1000);
    
    if (remainingSeconds <= 0) return 'Completing...';
    return `~${formatTime(remainingSeconds)} remaining`;
  };

  if (!activeAnalysis || !open) return null;

  return (
    <Fade in={open}>
      <Paper
        elevation={12}
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '95vw' : '600px',
          maxWidth: '90vw',
          minHeight: isMinimized ? 'auto' : 400,
          maxHeight: isMinimized ? 200 : '80vh',
          zIndex: theme.zIndex.tooltip + 1,
          bgcolor: theme.palette.background.paper,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
        }}
      >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AnalysisIcon color="primary" />
          <Box>
            <Typography variant="h6">
              Property Analysis in Progress
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {activeAnalysis.url}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handleMinimize} size="small">
            {isMinimized ? <ExpandIcon /> : <MinimizeIcon />}
          </IconButton>
          {overallStatus === 'completed' && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={!isMinimized}>
        <Box sx={{ p: 2, pb: 1, overflowY: 'auto', maxHeight: '60vh' }}>
          {/* Overall Progress */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ pb: '16px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Overall Progress
                </Typography>
                <Chip
                  label={`${Math.round(overallProgress)}%`}
                  color={overallStatus === 'completed' ? 'success' : 'primary'}
                  size="small"
                />
              </Box>
              
              <LinearProgress
                variant="determinate"
                value={overallProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  mb: 1,
                  backgroundColor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: getStatusColor(overallStatus),
                  },
                }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Elapsed: {formatTime(elapsedTime)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getEstimatedCompletion()}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Individual Tool Progress */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Analysis Tools
          </Typography>
          
          <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
            {activeAnalysis.selectedTools.map((tool, index) => {
              const toolProgress = progress[tool];
              const toolStatus = toolProgress?.status || 'pending';
              const toolProgressValue = toolProgress?.progress || 0;
              const toolError = toolProgress?.error;

              return (
                <React.Fragment key={tool}>
                  <ListItem sx={{ px: 2 }}>
                    <ListItemIcon>
                      {getStatusIcon(toolStatus)}
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {toolConfig[tool]?.label || tool}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {Math.round(toolProgressValue)}%
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {toolError || toolConfig[tool]?.description || 'Processing...'}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={toolProgressValue}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: theme.palette.grey[200],
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 2,
                                backgroundColor: getStatusColor(toolStatus),
                              },
                            }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < activeAnalysis.selectedTools.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
          
          {/* Error Display */}
          {activeAnalysis.error && (
            <Card sx={{ mt: 2, bgcolor: theme.palette.error.light, color: theme.palette.error.contrastText }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon />
                  <Typography variant="subtitle2">Analysis Error</Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {activeAnalysis.error}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </Collapse>

      <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {overallStatus === 'completed' ? (
          <Button onClick={onClose} variant="contained" color="primary">
            View Results
          </Button>
        ) : overallStatus === 'error' ? (
          <>
            <Button onClick={onClose} variant="outlined">
              Close
            </Button>
            <Button onClick={handleCancel} variant="contained" color="error">
              Retry Analysis
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleMinimize}
              variant="outlined"
              startIcon={isMinimized ? <ExpandIcon /> : <MinimizeIcon />}
            >
              {isMinimized ? 'Expand' : 'Minimize'}
            </Button>
            <Button onClick={handleCancel} variant="contained" color="error">
              Cancel
            </Button>
          </>
        )}
      </Box>
      </Paper>
    </Fade>
  );
};

export default AnalysisProgressModal;